import React from "react";

import Image from "next/image";
import Link from "next/link";

import SupportImage from "public/images/icons/enerflo-platform-icon-best-in-class-support.svg";
import FlexibilityImage from "public/images/icons/enerflo-platform-icon-flexibility.svg";
import IndustryImage from "public/images/icons/enerflo-platform-icon-industry-leading-technology.svg";
import LogicallyPricedImage from "public/images/icons/enerflo-platform-icon-logically-priced.svg";
import IntegrationsImage from "public/images/icons/enerflo-platform-icon-most-integrations.svg";
import OpenPlatformImage from "public/images/icons/enerflo-platform-icon-open-platform.svg";
import FinancingImage from "public/images/icons/enerflo-platform-icon-simplified-financing.svg";
import SingleLoginImage from "public/images/icons/enerflo-platform-icon-single-login.svg";

import Heading from "../../../heading";
import HeroSection from "../../../hero-section";
import styles from "./index.module.css";

const items = [
  {
    title: "Open Platform",
    description:
      "Enerflo is the first truly open platform in the industry and we have the most app integrations to prove it. We love to work and play well with others!",
    image: OpenPlatformImage,
    imageAlt: "Icon: Enerflo Solar Platform Open Platform.",
  },
  {
    title: "Industry-Leading Technology",
    description:
      "Enerflo is deeply invested in development and innovation to continue to bring our partners new, leading edge solutions to help them scale their business.",
    image: IndustryImage,
    imageAlt: "Icon: Enerflo Solar Platform Industry Leading Technology.",
  },
  {
    title: "Flexibility",
    description:
      "Enerflo is flexible enough to configure around our partners’ existing 2, 3 or however-many-step processes, so it feels like the platform was purpose built just for them.",
    image: FlexibilityImage,
    imageAlt: "Icon: Enerflo Solar Platform Flexibility.",
  },
  {
    title: "The Most Integrations",
    description: (
      <span>
        Our{" "}
        <Link href="/integrations">
          <a className="text-primary">integrations</a>
        </Link>{" "}
        set us apart from other solar platforms. Enerflo can be used out of the box or seamlessly integrated with the
        tools our partners already rely on. What’s best for our partners is best for us!
      </span>
    ),
    image: IntegrationsImage,
    imageAlt: "Icon: Enerflo Solar Platform has the Most Integrations.",
  },
  {
    title: "Simplified Financing",
    description: (
      <span>
        Our integrations with the{" "}
        <Link href="/features/lendflo">
          <a className="text-primary">top 5 solar lenders</a>
        </Link>{" "}
        eliminate portal logins and manual entry, enabling quick approvals all within the unified sales process.
      </span>
    ),
    image: FinancingImage,
    imageAlt: "Icon: Enerflo Solar Platform Simplified Financing.",
  },
  {
    title: "Single Login",
    description:
      "Whether our partners use Enerflo out of the box or they choose to integrate their existing applications, they only need a single login to seamlessly manage their entire sales and operations, start to finish.",
    image: SingleLoginImage,
    imageAlt: "Icon: Enerflo Solar Platform Single Login.",
  },
  {
    title: "Best-In-Class Support",
    description:
      "Every partner gets a dedicated Partner Success Manager (PSM) who acts as an extended member of their own team to ensure their success from day one.",
    image: SupportImage,
    imageAlt: "Icon: Enerflo Solar Platform Come with Best-in-Class Support.",
  },
  {
    title: "Logically Priced",
    description:
      "Unlike other solar software companies that charge by opportunity and/or users, Enerflo charges a flat monthly subscription fee for unlimited users, deals, etc.",
    image: LogicallyPricedImage,
    imageAlt: "Icon: Enerflo Solar Platform is Logically Priced.",
  },
];

const title = "Solar Platform Benefits";

const PlatformBenefits = () => (
  <div className="bg-gray-100">
    <HeroSection title={title} className="flex-col">
      <HeroSection.Content className="w-full md:w-3/4 md:text-center">
        <HeroSection.Header>
          <Heading tag="h2">{title}</Heading>
        </HeroSection.Header>
        <HeroSection.Description>
          You may be asking yourself “How is Enerflo different?” At Enerflo, we pride ourselves on being different by
          design. We understand that Solar Dealers and Installers already have unique processes in place and tools they
          love to use. Enerflo is not here to change the way you do business. We’re here to help you do what you do more
          efficiently so you can focus on growing your business.
        </HeroSection.Description>
      </HeroSection.Content>

      <div className={styles.benefitsGrid}>
        {items.map((item, index) => (
          <div key={index} className={styles.item}>
            <div>
              <figure className="h-16 w-16 md:h-20 md:w-20 lg:h-28 lg:w-28 relative mx-auto">
                <Image src={item.image} alt={item.imageAlt} layout="fill" />
              </figure>
              <Heading tag="h4" className="mt-4 lg:mt-8">
                {item.title}
              </Heading>
            </div>
            <p className="mt-2 md:mt-4 text-gray-500">{item.description}</p>
          </div>
        ))}
      </div>
    </HeroSection>
  </div>
);

export default PlatformBenefits;
